import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setU] = useState("");
  const [password, setP] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user && user !== null) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(username, password);
      toast.success("Autentificare reușită");
      navigate("/");
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Autentificare eșuată";
      if (status === 429) {
        toast.error(msg, { duration: 8000, description: "Așteaptă expirarea blocării sau cere unui administrator să deblocheze contul." });
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-black text-white font-sans antialiased relative overflow-hidden selection:bg-red-600 selection:text-white">
      {/* Swiss grid — subtle vertical lines */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]" aria-hidden="true">
        <div className="h-full grid grid-cols-12 gap-0">
          {Array.from({ length: 13 }).map((_, i) => (
            <div key={i} className="border-l border-white h-full" />
          ))}
        </div>
      </div>

      {/* Swiss cross — geometric accent */}
      <div className="absolute top-8 right-8 lg:top-12 lg:right-12 z-20" aria-hidden="true">
        <svg width="36" height="36" viewBox="0 0 36 36" className="text-red-600">
          <rect width="36" height="36" fill="currentColor" />
          <rect x="15" y="6" width="6" height="24" fill="white" />
          <rect x="6" y="15" width="24" height="6" fill="white" />
        </svg>
      </div>

      <div className="relative z-10 min-h-screen grid grid-cols-1 lg:grid-cols-12">

        {/* === LEFT: Brand + manifesto === */}
        <aside className="lg:col-span-7 xl:col-span-8 border-b lg:border-b-0 lg:border-r border-white/10 flex flex-col justify-between px-6 sm:px-10 lg:px-16 py-10 lg:py-14">
          {/* Top row — logo + tagline */}
          <header className="flex items-start justify-between">
            <a
              href="https://www.convicgmbh.ch"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 group/brand"
              data-testid="login-brand-link"
            >
              <div className="w-14 h-14 rounded-full overflow-hidden ring-2 ring-white/20 bg-white transition-all duration-300 group-hover/brand:ring-red-600 group-hover/brand:scale-105 shadow-[0_0_24px_rgba(255,255,255,0.06)]">
                <img src="/logo-convic.jpg" alt="ConVic" className="w-full h-full object-cover scale-[1.02]" draggable="false" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-bold tracking-wider uppercase group-hover/brand:text-red-500 transition-colors">ConVic GmbH</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/40">Schadstoffsanierung ↗</div>
              </div>
            </a>
            <div className="hidden lg:block text-right text-[10px] tracking-[0.3em] uppercase text-white/40">
              № 001<br/>
              Zürich · Schänis
            </div>
          </header>

          {/* Hero — bold Swiss typography */}
          <div className="flex-1 flex flex-col justify-center py-16 lg:py-0">
            <div className="max-w-4xl">
              <div className="text-[10px] tracking-[0.4em] uppercase text-red-500 mb-6 font-bold">// Platforma internă</div>
              <h1 className="font-extrabold tracking-[-0.04em] leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-[108px]">
                <span className="block">Platforma</span>
                <span className="block text-white/30">operativă</span>
                <span className="block">
                  <span className="inline-block text-red-600">ConVic</span>
                  <span className="inline-block"> GmbH</span>.
                </span>
              </h1>
            </div>

            <div className="mt-10 lg:mt-14 max-w-xl text-sm sm:text-base text-white/60 leading-relaxed">
              Platformă internă cu drepturi controlate per utilizator. Acces strict pe bază de autentificare.
            </div>
          </div>

          {/* Bottom — Swiss accent strip */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8 bg-red-600"/>
              <span className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold">Acces restricționat</span>
            </div>
            <div className="border border-white/10 bg-white/[0.02] px-5 py-6">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-white/70 mb-1">Doar pentru personalul autorizat</div>
              <div className="text-[11px] text-white/40 leading-relaxed">
                Conținutul, modulele și datele platformei sunt vizibile exclusiv după autentificare. Pentru acces, contactează administratorul.
              </div>
            </div>
          </div>
        </aside>

        {/* === RIGHT: Auth form === */}
        <main className="lg:col-span-5 xl:col-span-4 flex flex-col">

          {/* Vertical label column */}
          <div className="hidden lg:flex items-center justify-between px-10 pt-14 pb-4">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/30 font-bold">Access · Autentificare</div>
            <div className="text-[10px] tabular-nums text-white/30 font-mono">{year}</div>
          </div>

          <div className="flex-1 flex items-center px-6 sm:px-10 lg:px-10 py-10 lg:py-0">
            <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">

              {/* Number marker */}
              <div className="flex items-baseline gap-4 mb-10">
                <div className="text-[11px] tracking-[0.3em] font-mono text-red-500">01 /</div>
                <div className="h-px flex-1 bg-white/10" />
                <div className="text-[11px] tracking-[0.3em] font-mono text-white/30">02</div>
              </div>

              {/* Hero round logo — Swiss elegance */}
              <div className="flex justify-center mb-10" data-testid="login-hero-logo">
                <div className="relative group/logo">
                  {/* outer red ring rotation accent */}
                  <div className="absolute -inset-2 rounded-full border border-red-600/30 group-hover/logo:border-red-600/70 transition-colors duration-500" aria-hidden="true" />
                  <div className="absolute -inset-[14px] rounded-full border border-white/5" aria-hidden="true" />
                  {/* glow */}
                  <div className="absolute inset-0 rounded-full bg-red-600/10 blur-2xl opacity-0 group-hover/logo:opacity-100 transition-opacity duration-500" aria-hidden="true" />
                  {/* the logo itself */}
                  <div className="relative w-[120px] h-[120px] rounded-full overflow-hidden ring-1 ring-white/15 bg-white shadow-[0_8px_40px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover/logo:scale-[1.03]">
                    <img
                      src="/logo-convic.jpg"
                      alt="ConVic GmbH"
                      className="w-full h-full object-cover scale-[1.02] select-none"
                      draggable="false"
                    />
                  </div>
                  {/* tiny red dot accent */}
                  <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-600 ring-2 ring-black" aria-hidden="true" />
                </div>
              </div>

              <h2 className="font-extrabold text-4xl sm:text-5xl tracking-[-0.03em] leading-none mb-3 text-center">
                Intră în<br/>cont.
              </h2>
              <p className="text-sm text-white/50 mb-12 text-center">
                Introdu datele de acces pentru a continua.
              </p>

              {/* Inputs — Swiss underline style */}
              <div className="space-y-8">
                <div className="group">
                  <label htmlFor="username" className="block text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold mb-2">
                    Utilizator
                  </label>
                  <input
                    id="username"
                    type="text"
                    data-testid="login-username"
                    value={username}
                    onChange={(e) => setU(e.target.value)}
                    autoComplete="username"
                    required
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-white focus:outline-none py-2.5 text-lg font-medium text-white placeholder:text-white/20 transition-colors"
                    placeholder="nume.utilizator"
                  />
                </div>

                <div className="group">
                  <div className="flex items-end justify-between mb-2">
                    <label htmlFor="password" className="block text-[10px] tracking-[0.3em] uppercase text-white/40 font-bold">
                      Parolă
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      className="text-[10px] tracking-[0.2em] uppercase text-white/40 hover:text-white transition-colors font-bold"
                      tabIndex={-1}
                    >
                      {showPw ? "Ascunde" : "Arată"}
                    </button>
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPw ? "text" : "password"}
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setP(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="w-full bg-transparent border-0 border-b border-white/20 focus:border-white focus:outline-none py-2.5 text-lg font-medium text-white placeholder:text-white/20 tracking-widest transition-colors"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {/* Submit — big Swiss button */}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit"
                className="group mt-12 w-full flex items-center justify-between bg-white text-black px-6 py-5 font-extrabold tracking-[-0.01em] text-base hover:bg-red-600 hover:text-white transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <span className="uppercase tracking-wider">
                  {loading ? "Se verifică" : "Intră în cont"}
                </span>
                <ArrowRight
                  size={20}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>

            </form>
          </div>

          {/* Bottom footer */}
          <footer className="px-6 sm:px-10 lg:px-10 py-6 border-t border-white/10 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4 flex-wrap text-[10px] tracking-[0.2em] uppercase text-white/30 font-bold">
              <span>© {year} ConVic GmbH</span>
              <span className="text-white/10">·</span>
              <a
                href="mailto:info@convicgmbh.ch?subject=Probleme%20autentificare%20platform%C4%83"
                className="text-white/40 hover:text-red-500 transition-colors tracking-[0.2em] uppercase"
                data-testid="login-contact-mail"
              >
                Probleme acces? Contact
              </a>
            </div>
            <div className="hidden sm:flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-white/30 font-mono">
              <span>Swiss</span>
              <span className="text-red-600">×</span>
              <span>Black</span>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
